import { useEffect, useState } from 'react';
import type { Resource } from '@core/types/resource';
import { cn } from '@core/ui';
import { userprofileApi } from '@core/api';
import { useMediaUrl } from './MediaImage';

type EntityAvatarProps = {
  name?: string | null;
  image?: string | null;
  resource?: Resource | null;
  profileId?: string | null;
  userId?: string | null;
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
  shape?: 'circle' | 'square';
  className?: string;
};

const pendingProfiles = new Map<string, Array<(image?: string) => void>>();
const pendingUsers = new Map<string, Promise<string | undefined>>();
let flushScheduled = false;

function resolveProfileImage(profileId: string): Promise<string | undefined> {
  return new Promise((resolve) => {
    const callbacks = pendingProfiles.get(profileId) ?? [];
    callbacks.push(resolve);
    pendingProfiles.set(profileId, callbacks);
    if (flushScheduled) return;
    flushScheduled = true;
    queueMicrotask(async () => {
      flushScheduled = false;
      const batch = new Map(pendingProfiles);
      pendingProfiles.clear();
      const ids = [...batch.keys()];
      try {
        const result = await userprofileApi.ListUserprofilesByIDs({ ids });
        const images = new Map((result.userprofiles?.profiles ?? []).map((profile) => [
          profile.entity?.id,
          avatarImage(profile.avatar),
        ]));
        batch.forEach((listeners, id) => listeners.forEach((listener) => listener(images.get(id))));
      } catch {
        batch.forEach((listeners) => listeners.forEach((listener) => listener()));
      }
    });
  });
}

function resolveUserImage(userId: string): Promise<string | undefined> {
  let request = pendingUsers.get(userId);
  if (!request) {
    request = userprofileApi.GetUserprofileDefault(userId)
      .then((result) => avatarImage(result.userprofile?.avatar))
      .catch(() => undefined)
      .finally(() => pendingUsers.delete(userId));
    pendingUsers.set(userId, request);
  }
  return request;
}

const sizes = {
  xs: 'h-6 w-6 text-[10px]',
  sm: 'h-8 w-8 text-xs',
  md: 'h-11 w-11 text-sm',
  lg: 'h-14 w-14 text-base',
  xl: 'h-28 w-28 sm:h-36 sm:w-36 text-3xl sm:text-4xl',
};

export function avatarInitials(name?: string | null): string {
  const words = (name ?? '').trim().split(/\s+/).filter(Boolean);
  if (!words.length) return '?';
  if (words.length === 1) return words[0].slice(0, 2).toLocaleUpperCase();
  return `${words[0][0]}${words[words.length - 1][0]}`.toLocaleUpperCase();
}

/** The resource contract stores a thumbnail in icon and the original in title. */
export function avatarImage(resource?: Resource | null): string | undefined {
  return resource?.icon || resource?.title || undefined;
}

export function EntityAvatar({ name, image, resource, profileId, userId, size = 'md', shape = 'circle', className }: EntityAvatarProps) {
  const [resolved, setResolved] = useState<{ key: string; url?: string }>();
  const identityKey = profileId ? `profile:${profileId}` : userId ? `user:${userId}` : '';
  useEffect(() => {
    if (!identityKey || image || avatarImage(resource)) return;
    let active = true;
    const request = profileId
      ? resolveProfileImage(profileId)
      : resolveUserImage(userId!);
    request.then((url) => { if (active) setResolved({ key: identityKey, url }); });
    return () => { active = false; };
  }, [identityKey, profileId, userId, image, resource]);
  const [failedSources, setFailedSources] = useState<string[]>([]);
  const preferred = image || avatarImage(resource) || (resolved?.key === identityKey ? resolved.url : undefined);
  const alternate = [resource?.icon, resource?.title].find((candidate) => candidate && candidate !== preferred);
  const preferredUrl = useMediaUrl(preferred);
  const alternateUrl = useMediaUrl(alternate);
  const visibleSource = [preferredUrl, alternateUrl].find((url) => url && !failedSources.includes(url));

  return (
    <span
      role="img"
      aria-label={name || undefined}
      className={cn(
        'inline-flex shrink-0 items-center justify-center overflow-hidden border border-border bg-primary/10 font-semibold text-primary',
        sizes[size],
        shape === 'circle' ? 'rounded-full' : 'rounded-xl',
        className,
      )}
    >
      {visibleSource ? (
        <img src={visibleSource} alt="" className="h-full w-full object-cover" onError={() => setFailedSources((current) => [...current, visibleSource])} />
      ) : (
        <span aria-hidden="true">{avatarInitials(name)}</span>
      )}
    </span>
  );
}
